import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificationTemplate } from '../../../core/model/notificationTemplate';

/**
 * Interface que representa exactamente un elemento de la respuesta de la API de invitaciones B2B pendientes.
 * Basada en el modelo C# PendingInvitationResponse del backend.
 *
 * NOTA: La API devuelve un ARRAY de estas invitaciones: PendingInvitationResponse[]
 *
 * Estructura real de cada elemento del array:
 * {
 *   id: string;
 *   notificationId: string;
 *   companyId: string;
 *   providerId: string;
 *   providerCompanyId: string | null;
 *   status: string; ("Pending", "Accepted", "Rejected", etc.)
 *   activationDate: string; (ISO date)
 *   expirationDate: string; (ISO date)
 *   createdAt: string; (ISO date)
 *   updatedAt: string | null; (ISO date)
 *   notes: string | null;
 *   template: NotificationTemplate;
 * }
 */
export interface B2bInvitationData {
  id: string;
  notificationId: string;
  companyId: string;
  providerId: string;
  providerCompanyId?: string | null;
  status: string;
  activationDate: string;
  expirationDate: string;
  createdAt: string;
  updatedAt?: string | null;
  notes?: string | null;
  template: NotificationTemplate;
  category?: string | null;
  channelName?: string | null;
  channelDescription?: string | null;
  channelImage?: string | null;
}

@Component({
  selector: 'app-b2b-invitation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    TranslateModule
  ],
  templateUrl: './b2b-invitation-dialog.component.html',
  styleUrls: ['./b2b-invitation-dialog.component.scss']
})
export class B2bInvitationDialogComponent {

  currentLanguage: string = 'es';

  constructor(
    public dialogRef: MatDialogRef<B2bInvitationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: B2bInvitationData,
    private translateService: TranslateService
  ) {
    this.currentLanguage = this.translateService.currentLang || 'es';
  }

  get daysLeft(): number {
    if (!this.data?.expirationDate) return 0;
    const expiration = new Date(this.data.expirationDate);
    const now = new Date();
    const diffTime = expiration.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  }

  get headerStyle(): any {
    return {
      'background-color': this.data?.template?.header?.corporateColor || '#123456',
      'color': this.data?.template?.header?.textColor || '#FFFFFF'
    };
  }

  get corporateColor(): string {
    return this.data?.template?.header?.corporateColor || '#c53030';
  }

  getDarkerShade(color: string): string {
    // Convertir hex a RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Oscurecer el color (reducir cada componente en un 30%)
    const darkR = Math.floor(r * 0.7);
    const darkG = Math.floor(g * 0.7);
    const darkB = Math.floor(b * 0.7);

    // Convertir de vuelta a hex
    return `#${darkR.toString(16).padStart(2, '0')}${darkG.toString(16).padStart(2, '0')}${darkB.toString(16).padStart(2, '0')}`;
  }

  getLighterShade(color: string): string {
    // Convertir hex a RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Aclarar el color (aumentar cada componente hacia 255)
    const lightR = Math.min(255, Math.floor(r + (255 - r) * 0.6));
    const lightG = Math.min(255, Math.floor(g + (255 - g) * 0.6));
    const lightB = Math.min(255, Math.floor(b + (255 - b) * 0.6));

    // Convertir de vuelta a hex
    return `#${lightR.toString(16).padStart(2, '0')}${lightG.toString(16).padStart(2, '0')}${lightB.toString(16).padStart(2, '0')}`;
  }

  getLocalizedText(textObj: { [key: string]: string } | null | undefined): string {
    if (!textObj) return '';
    return textObj[this.currentLanguage] || textObj['es'] || '';
  }

  get hasExpiration(): boolean {
    return !!this.data?.expirationDate;
  }

  onAction(action: string | undefined): void {
    this.dialogRef.close(action || '');
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
